import React, {FC, useEffect, useRef, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {ButtonLink} from '../../ButtonLink'
import {Flex} from '../Flex'

interface Props {
  /** Amount of lines to what content must be truncated */
  lines: number
  /** We can override default click behaviour and cancel expanding by passing `onReadMoreClick` handler */
  onReadMoreClick?: () => void
}

const Wrapper = styled(Flex.Column)`
  position: relative;
  overflow: hidden;
`

const Content = styled.div<{lines: number}>`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${({lines}) => lines ?? ''};
  -webkit-box-orient: vertical;
  white-space: initial;
  &.isExpanded {
    display: block;
  }
`

const FullHeightContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  pointer-events: none;
`

export const TruncatedContent: FC<Props> = ({
  children,
  lines,
  onReadMoreClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const fullHeightContentRef = useRef<HTMLDivElement>(null)

  const handleExpandClick = () => {
    if (onReadMoreClick) {
      onReadMoreClick()
      return
    }
    setIsExpanded(true)
  }

  useEffect(() => {
    const fullHeightContent = fullHeightContentRef.current
    const content = contentRef.current
    if (
      fullHeightContent &&
      content &&
      fullHeightContent.clientHeight === content.clientHeight
    ) {
      setIsExpanded(true)
    }
  }, [])

  return (
    <Wrapper gap="s300">
      <Content
        lines={lines}
        className={cx(isExpanded && 'isExpanded')}
        ref={contentRef}
      >
        {children}
      </Content>
      {/* Create invisible absolute positioned block to measure full content height */}
      <FullHeightContent ref={fullHeightContentRef}>
        {children}
      </FullHeightContent>
      {!isExpanded && (
        <ButtonLink
          size="sm"
          variant="default"
          isCompact
          onClick={handleExpandClick}
        >
          <FormattedMessageWrapper defaultMessage="Read more" id="readMore" />
        </ButtonLink>
      )}
    </Wrapper>
  )
}
